import classNames from "classnames";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ElementsAction,
  addSelectItems,
  closeTab,
  setCurrentTableStateAC,
  setTableSortingAC,
} from "store/actions/table";
import { showToast, toArray } from "utils/helpers";
import "./DashboardInfoTable.scss";

import { ModalContext } from "components/ModalContext";
import ConditionModal from "components/modals/ConditionModal";
import DeleteColumnModal from "components/modals/DeleteColumnModal";
import TableInfoFooter from "components/uiComponents/TableInfoFooter";
import TableInfoHeader from "components/uiComponents/TableInfoHeader";
import TableInfoRow from "components/uiComponents/TableInfoRow";
import { loadColorSchemeAC } from "store/actions/colorSidebar";
import {
  deleteColumnAC,
  restConditionModalAC,
  setConditionVariablesAC,
} from "store/actions/condition";
import { ColorConditionService } from "utils/services/colorCondition.service";
import { ComputeService } from "utils/services/compute.service";
import { collectionDelete } from "store/actions/collections";
import {
  clearColors,
  colorElements,
  isolateElements,
} from "utils/builder/IScheduleLogic";
import { tabType } from "utils/data";
import { indeterminateCheckboxTypes as checkboxTypes } from "components/uiComponents/IndeterminateCheckbox";
import useGetFilteredRows from "./useGetFiltredRows";
import { ViewTableLayout } from "./ViewTableLayout";

const DashboardInfoTable = ({ table }) => {
  const dispatch = useDispatch();
  const { handleModal } = useContext(ModalContext);

  const { compare } = useSelector((state) => state.viewer);
  const { header, lineElements, lines, totals, units, types } = table;
  const { tabs } = useSelector((state) => state.table);

  const [selectedItems, setSelectedItems] = useState(table.selectItems || {});
  const {
    totalColumnsValue,
    tableHeaders,
    tableColumnTypesArr,
    rowsData,
    sortingData,
  } = useSelector((state) => state.table.currentTableState);
  const [searchValue, setSearchValue] = useState("");

  const temporaryColumnScheme = useSelector(
    (state) => state.condition.columnSchemes
  );
  const colorScheme = useSelector((state) => state.colorSidebar.colorScheme);

  const schemesForTabs = useSelector(
    (state) => state.tabsSettings.tabsSettings
  );

  const currentColorScheme = schemesForTabs?.[table?.id]?.colorScheme;

  const currentTabColumnSchemes = schemesForTabs?.[table?.id]?.columnSchemes;

  const currentOpenIndex = useSelector(
    (state) => state.colorSidebar.currentColumnValue.key
  );

  const uniqueColorsData = useMemo(() => {
    return {
      store: {},
      positionObj: {
        horizontalIndex: null,
        verticalIndex: null,
      },
    };
  }, [currentOpenIndex]);

  const modelColors = [];

  const columnSchemes = useMemo(() => {
    return { ...currentTabColumnSchemes, ...temporaryColumnScheme };
  }, [currentTabColumnSchemes, temporaryColumnScheme]);

  useEffect(() => {
    if (currentColorScheme) {
      dispatch(loadColorSchemeAC(currentColorScheme));
    }
  }, [currentColorScheme]);

  useEffect(() => {
    if (!compare.isModeCompare && Object.values(selectedItems).length) {
      dispatch(ElementsAction(Object.values(selectedItems).flat(2)));
    }
  }, [compare.isModeCompare, dispatch, selectedItems]);

  useEffect(() => {
    const deleteCollection = async () => {
      if (!lineElements.length && table.type === tabType.unsaved) {
        const tab = tabs.find((tab) => table.id === tab.id);
        dispatch(closeTab(tab));
      }
      if (!lineElements.length && table.type !== tabType.unsaved) {
        try {
          await dispatch(
            collectionDelete({
              ...table.collection,
              folder: table.folder,
              tabs: tabs,
              id: table.id,
            })
          );
          showToast(true, "Selection set was deleted");
        } catch (error) {
          showToast(false, error);
        }
      }
    };

    deleteCollection();
  }, [
    dispatch,
    lineElements,
    table.collection,
    table.folder,
    table.id,
    table.type,
    tabs,
  ]);

  useEffect(() => {
    const sortObj = {};

    tableHeaders?.forEach((item) => {
      sortObj[item] = {
        isActive: false,
        isDescending: true,
      };
    });

    setTableSortingAC(null, sortObj);
  }, [tableHeaders]);

  useEffect(() => {
    dispatch(setConditionVariablesAC(tableHeaders, tableColumnTypesArr));
  }, [tableHeaders, tableColumnTypesArr, dispatch]);

  useEffect(() => {
    if (modelColors.length > 0) {
      clearColors();
      modelColors.forEach((colorObj) => {
        const [elementsIdArray, color] = Object.values(colorObj);
        colorElements(elementsIdArray, color);
      });
    }
  }, [modelColors]);

  const uniqLines = useCallback(() => {
    if (!lines || !lineElements) {
      return [];
    }

    const newArr = lines.map((el, index) => {
      const newEl = {
        lineElements: lineElements[index],
        lines: el,
      };

      return newEl;
    });

    return newArr;
  }, [lines, lineElements]);

  useEffect(() => {
    setSelectedItems(table.selectItems || {});
    const currentRows = uniqLines();
    const { updateHeaders, updatedRows, updatedTypes, updatedTotals } =
      ComputeService.executeFunctions({
        headers: header,
        rows: currentRows,
        totals: totals,
        types: types,
        columnSchemes: columnSchemes,
      });
    dispatch(
      setCurrentTableStateAC({
        tableHeaders: updateHeaders,
        tableColumnTypesArr: updatedTypes,
        rowsData: updatedRows,
        totalColumnsValue: updatedTotals,
      })
    );
  }, [
    lineElements,
    lines,
    header,
    totals,
    columnSchemes,
    table.selectItems,
    uniqLines,
    types,
    dispatch
  ]);

  useEffect(() => {
    dispatch(restConditionModalAC());
  }, [lines, lineElements, dispatch]);

  const selectedTotals = useMemo(() => {
    const selectedTotalsArr = [];

      const currentLines = rowsData.map(({ lines }) => lines)
      const linesFiltered = currentLines?.filter((item, i) => {
      let isInsideSelectedList = false;

      for (let key in selectedItems) {
        const keyStringArr = key.split("/");
        let keyString = keyStringArr[0];

        if (keyStringArr.length > 1) {
          keyStringArr.pop();
          keyString = keyStringArr.join("/");
        } else {
          keyString = keyStringArr[0];
        }

        if (item[0] == keyString) {
          isInsideSelectedList = true;
        }
      }

      return isInsideSelectedList;
    });

    linesFiltered?.forEach((item, i) => {
      item.forEach((el, index) => {
        if (typeof el === 'string') {
          if (typeof selectedTotalsArr[index] === 'number') {
            selectedTotalsArr[index] = selectedTotalsArr[index];
          } else {
            selectedTotalsArr[index] = '';
          }
        } else if (typeof el === 'number') {
          if (selectedTotalsArr[index] || selectedTotalsArr[index] === 0) {
            selectedTotalsArr[index] = selectedTotalsArr[index] + el;
          } else if (!selectedTotalsArr[index] && (el || el === 0)) {
            selectedTotalsArr[index] = el;
          } else if ((selectedTotalsArr[index] || selectedTotalsArr[index] === 0) && !el) {
            selectedTotalsArr[index] = selectedTotalsArr[index];
          } else {
            selectedTotalsArr[index] = 0;
          }
        }
      });
    });

    return selectedTotalsArr;
  }, [rowsData, selectedItems]);

  const generateLineId = (lines, lineElements) => {
    return `${lines[0]}/${lineElements.join("")}`
  };

  const getCheckboxStatus = () => {
    const dataForCheck = searchValue ? rowsDataFilteredBySearch : rowsData;
    const selectItemsKeys = Object.keys(table.selectItems || {});

    const currentSelectedItems = dataForCheck.filter(
      ({ lines, lineElements }) => {
        const id = generateLineId(lines, lineElements);
        return selectItemsKeys.includes(id);
      }
    );

    
    if (dataForCheck.length === currentSelectedItems.length) {
      return checkboxTypes.checked
    }

    return currentSelectedItems.length === 0 ? checkboxTypes.unchecked : checkboxTypes.indeterminate;
  };

  const dispatchData = (obj) => {
    const selectElementsInTable = toArray(obj);

    if (selectElementsInTable.length > 0) {
      dispatch(ElementsAction(selectElementsInTable));
      return
    }
    
    isolateElements(table.lineElements.flat());
  };

  const handleRowCheckChange = useCallback(
    (isChecked, lineElements, id) => {
      setSelectedItems((prevState) => {
        let obj = {};
        if (isChecked) {
          obj = { ...prevState, [id]: lineElements };
        } else {
          let newObj = {};

          for (const key in prevState) {
            if (key !== id) {
              newObj[key] = prevState[key];
            }
          }

          obj = newObj;
        }
        dispatch(addSelectItems({ selectItems: obj, id: table.id }));
        dispatchData(obj);
        return obj;
      });
    },
    [table]
  );

  const unitsArr = useMemo(() => {
    return units?.map(item => {
      if (item) {
        const unitParts = item.split('^');

        return {
          unit: unitParts[0],
          subUnit: unitParts[1],
        };
      }
      return null;
    })
  }, [units]);

  const { rowsDataFilteredBySort, rowsDataFilteredBySearch } = useGetFilteredRows({
      rowsData: rowsData,
      searchValue,
      sortingData: sortingData,
      tableHeaders: tableHeaders,
    });

  const onChangeCheckbox = (isChecked, dataForHandle) => {
    if (!isChecked) {
      let obj = {};

      dataForHandle.map(({ lines, lineElements }) => {
        const id = generateLineId(lines, lineElements);

        return (obj[id] = lineElements);
      });

      setSelectedItems((prevState) => {
        const tempOb = { ...prevState, ...obj };

        dispatch(ElementsAction(toArray(tempOb)));
        dispatch(addSelectItems({ selectItems: tempOb, id: table.id }));

        return tempOb;
      });
    } else {
      setSelectedItems((prevState) => {
        const tempOb = {};

        for (const key in prevState) {
          const condition = dataForHandle.some(
            (el) => generateLineId(el.lines, el.lineElements) === key
          );

          if (!condition) {
            tempOb[key] = prevState[key];
          }
        }

        dispatchData(tempOb);
        dispatch(addSelectItems({ selectItems: tempOb, id: table.id }));

        return tempOb;
      });
    }
  };

  const rows = useMemo(() => {
    return (
      rowsDataFilteredBySort &&
      rowsDataFilteredBySort.map(({ lines, lineElements }) => {
        const id = generateLineId(lines, lineElements);

        const name = generateLineId(lines, lineElements);

        const colorData = ColorConditionService.getColorByColorScheme({
          colorScheme,
          uniqueColorsData,
          arrayForGradient: rowsDataFilteredBySort,
          items: lines,
        });

        if (colorData) {
          modelColors.push({
            [name]: lineElements,
            color: colorData.color,
          });
        }

        return (
          <TableInfoRow
            lineElements={lineElements}
            key={id}
            paintCeilData={colorData}
            lines={lines}
            unitsArr={unitsArr}
            id={id}
            onCheckChange={handleRowCheckChange}
            checked={selectedItems[id] ? selectedItems[id] : false}
          />
        );
      })
    );
  }, [
    rowsDataFilteredBySort,
    colorScheme,
    uniqueColorsData,
    unitsArr,
    handleRowCheckChange,
    selectedItems,
    modelColors,
  ]);

  const handleSortChange = (name) => {
    dispatch(setTableSortingAC(name));
  };

  const deleteSelectElements = () => {
    setSelectedItems({});
  }


  const handleCancel = () => {
    handleModal(null, true)
  }

  const onShowConditionModal = ({
    columnScheme,
    autoActiveInputs = true
  }) => {

    const tree = columnScheme?.currentTree
    const columnName = columnScheme?.newColumnName
    const functionValue = columnScheme?.functionValue;
    const currentTabId = columnScheme?.currentTabId;

    const showColumnDeletePopUp = (columnName) => {
      const deleteCallback = () => {
        dispatch(deleteColumnAC(columnName, table?.id));
        handleCancel();
      };

      handleModal(
        <DeleteColumnModal
          columnName={columnName}
          deleteCallback={deleteCallback}
          onClose={handleCancel}
        />
      );
    };

    handleModal(
      <ConditionModal
        currentTableId={table?.id}
        header={tableHeaders}
        types={tableColumnTypesArr}
        deleteCallback={showColumnDeletePopUp}
        currentTabId={currentTabId}
        autoActiveInputs={autoActiveInputs}
        functionValue={functionValue}
        columnName={columnName}
        onClose={handleCancel}
        tree={tree}
      />,
      false,
      true
    );
  };

  const renderHeader = () => {
    if (!tableHeaders?.length) {
      return null;
    }

    const onChangeHeaderCheckbox = (isChecked) => {
      const dataForCheck = searchValue
      ? rowsDataFilteredBySearch
      : rowsData;
      onChangeCheckbox(isChecked, dataForCheck);
    }

    return (
      <TableInfoHeader
        header={tableHeaders}
        onChangeCheckbox={onChangeHeaderCheckbox}
        status={getCheckboxStatus()}
        columnSchemes={columnSchemes}
        onEditColumn={onShowConditionModal}
        currentOpenIndex={currentOpenIndex}
        arrayTypes={tableColumnTypesArr}
        sorting={sortingData}
        onSortChange={handleSortChange}
      />
    );
  };

  const renderRows = () => {
    if (!rows?.length) {
      return null;
    }

    return (
      <div className="table-body" >
        {rows}
      </div>
    );
  };

  const renderFooter = () => {
    if (!totalColumnsValue?.length || !rows?.length) {
      return null;
    }

    return (
      <TableInfoFooter
        totals={totalColumnsValue}
        rows={rowsData}
        checkList={selectedItems}
        selectedTotals={selectedTotals}
        header={tableHeaders}
        unitsArr={unitsArr}
      />
    );
  };

  return (
    <ViewTableLayout
      headerComponent={renderHeader()}
      footerComponent={renderFooter()}
      rowsComponent={renderRows()}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      table={table}
      deleteSelectElements={deleteSelectElements}
      totalColumnsValue={totalColumnsValue}
      tableHeaders={tableHeaders}
      onShowConditionModal={onShowConditionModal}
      selectedItems={selectedItems}
      dataForDownloadTable={rowsDataFilteredBySearch}
    />
  );
};

export default memo(DashboardInfoTable);
